import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import GeneralTable from "../../../components/Table/GeneralTable";
import "./Holidays.scss";
import IconButton from "../../../components/IconButton/IconButton";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import Heading from "../../../components/HeadingCom/Heading";
import { toast } from "react-toastify";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import { Building, Users, Check, CheckSquare, Square } from "lucide-react";
import BranchBadges from "./Office Timings/BranchBadges";

const Holidays = ({ markPageAsCompleted }) => {
  const [holidayName, setHolidayName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [noOfdays, setNoOfdays] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [searchTerm] = useState("");
  const [branches, setBranches] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [error, setError] = useState("");
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [holidayType, setHolidayType] = useState("");
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [showBulkDeleteWarning, setShowBulkDeleteWarning] = useState(false);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/branches`,
        );
        if (Array.isArray(response.data.data)) {
          setBranches(response.data.data);
        } else {
          console.error("Unexpected data format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/holidays/all`,
        );

        if (
          Array.isArray(response.data?.data) &&
          response.data.data.length > 0
        ) {
          setHolidays(response.data.data);
          markPageAsCompleted("Holidays");
        } else {
          console.error(
            "Unexpected data format or empty holidays list:",
            response.data,
          );
        }
      } catch (error) {
        console.error("Error fetching holidays:", error);
      }
    };
    fetchHolidays();
  }, [markPageAsCompleted]); // FIX: Added missing dependency

  const validateForm = () => {
    const errors = {};
    if (!holidayName.trim()) {
      errors.holidayName = "Holiday name is required";
    }
    if (!eventDate) {
      errors.eventDate = "Event date is required";
    }
    if (!noOfdays || noOfdays < 1) {
      errors.noOfdays = "Number of days must be at least 1";
    }
    if (branchIds.length === 0) {
      errors.branchIds = "At least one branch must be selected";
    }
    if (!holidayType) {
      errors.holidayType = "Holiday type is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSelectAllBranches = () => {
    const allBranchIds = branches.map((branch) => branch.branchId);
    const allSelected = allBranchIds.every((id) => branchIds.includes(id));
    if (allSelected) {
      setBranchIds([]);
    } else {
      setBranchIds(allBranchIds);
      if (formErrors.branchIds) {
        setFormErrors((prev) => ({ ...prev, branchIds: null }));
      }
    }
  };

  const handleBranchSelect = (branchId) => {
    setBranchIds((prev) => {
      const newBranchIds = prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId];
      if (formErrors.branchIds) {
        setFormErrors((prevErrors) => ({ ...prevErrors, branchIds: null }));
      }
      return newBranchIds;
    });
  };

  const handleHolidayCheckboxChange = (holidayId, isChecked) => {
    setSelectedHolidays((prevSelected) => {
      if (isChecked) {
        return [...prevSelected, holidayId];
      } else {
        return prevSelected.filter((id) => id !== holidayId);
      }
    });
  };

  const handleSelectAllHolidays = (isChecked) => {
    if (isChecked) {
      const allFilteredIds = filteredHolidays.map(
        (holiday) => holiday.holidayId,
      );
      setSelectedHolidays((prev) =>
        Array.from(new Set([...prev, ...allFilteredIds])),
      );
    } else {
      const allFilteredIds = filteredHolidays.map(
        (holiday) => holiday.holidayId,
      );
      setSelectedHolidays((prev) =>
        prev.filter((id) => !allFilteredIds.includes(id)),
      );
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedHolidays.length === 0) {
      toast.error("Please select holidays to delete.");
      return;
    }
    setShowBulkDeleteWarning(true);
  };

  const handleBulkDeleteSubmit = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/holidays`,
        { data: selectedHolidays },
      );
      const updatedResponse = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/all`,
      );
      setHolidays(updatedResponse.data.data);
      setSelectedHolidays([]);
      toast.success(`${selectedHolidays.length} holidays deleted successfully`);
      if (updatedResponse.data.data.length > 0) {
        markPageAsCompleted("Holidays");
      }
    } catch (error) {
      console.error("Error deleting holidays:", error);
    } finally {
      setShowBulkDeleteWarning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    const formattedNoOfdays = parseInt(noOfdays);
    const holidayData = {
      holidayName: holidayName.trim(),
      eventDate,
      noOfdays: formattedNoOfdays,
      holidayType,
      branchIds,
    };
    try {
      if (editingHolidayId) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/holidays`,
          {
            ...holidayData,
            holidayId: editingHolidayId,
          },
        );
        toast.success("Holiday edited successfully");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/holidays`,
          holidayData,
        );
        toast.success("Holiday added successfully");
      }
      resetForm();
      const updatedResponse = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/all`,
      );
      setHolidays(updatedResponse.data.data);
      if (updatedResponse.data.data.length > 0) {
        markPageAsCompleted("Holidays");
      }
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            setError("Invalid holiday data. Please check your input fields.");
            break;
          case 401:
            setError("Unauthorized. Please log in.");
            break;
          case 403:
            setError(
              "Access denied. You don't have permission to perform this action.",
            );
            break;
          case 404:
            setError("Holiday not found.");
            break;
          case 409:
            setError(
              "Holiday conflict. Please ensure there are no duplicate entries.",
            );
            break;
          case 500:
            setError("Server error. Please try again later.");
            break;
          default:
            setError("An unexpected error occurred. Please try again.");
        }
      } else {
        setError("Network error. Please check your internet connection.");
      }
      console.error(
        "Error submitting holiday:",
        error.response ? error.response.data : error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setHolidayName("");
    setEventDate("");
    setNoOfdays("");
    setBranchIds([]);
    setEditingHolidayId(null);
    setIsFormVisible(false);
    setHolidayType("");
    setFormErrors({});
    setError("");
  };

  const onCancel = () => {
    resetForm();
  };

  const filteredHolidays = holidays.filter((holiday) =>
    holiday.holidayName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const isAllSelected =
    filteredHolidays.length > 0 &&
    filteredHolidays.every((holiday) =>
      selectedHolidays.includes(holiday.holidayId),
    );

  const renderBranchesSection = () => {
    if (isLoading) {
      return (
        <div className="holiday-form__loading">
          <div className="holiday-form__loading-spinner"></div>
          <span>Loading branches...</span>
        </div>
      );
    }
    if (branches.length === 0) {
      return (
        <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-300">
          <Building className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Active Branches
          </h3>
          <p className="text-gray-600 max-w-md mx-auto">
            There are no active branches available at the moment. Please check
            back later or contact your administrator.
          </p>
        </div>
      );
    }
    const allBranchIds = branches.map((branch) => branch.branchId);
    const allBranchesSelected =
      allBranchIds.length > 0 &&
      allBranchIds.every((id) => branchIds.includes(id));

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building className="h-6 w-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Select Branches
            </h3>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-2.5 rounded-full">
              {branchIds.length} of {branches.length} selected
            </span>
          </div>
          {branches.length > 0 && (
            <button
              type="button"
              onClick={handleSelectAllBranches}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-[20px] transition-colors duration-200"
            >
              {allBranchesSelected ? (
                <>
                  <CheckSquare className="h-4 w-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="h-4 w-4" />
                  Select All
                </>
              )}
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
          {branches.map((branch) => (
            <button
              key={branch.branchId}
              type="button"
              onClick={() => handleBranchSelect(branch.branchId)}
              className={`p-1.5 rounded-xl border transition-all duration-200 text-left hover:shadow ${
                branchIds.includes(branch.branchId)
                  ? "border-blue-500 shadow-md"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm truncate pr-1">
                  {branch.branchName}
                </span>
                {branchIds.includes(branch.branchId) && (
                  <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <Users className="h-3 w-3" />
                <span>{branch.departments?.length || 0} departments</span>
              </div>
            </button>
          ))}
        </div>
        {formErrors.branchIds && (
          <div className="text-red-600 text-sm mt-2">
            {formErrors.branchIds}
          </div>
        )}
      </div>
    );
  };

  const transformData = (holidays) => {
    return holidays.map((holiday) => {
      const branchNames = holiday.branches
        ?.map((branch) => branch.branchName)
        .join(", ");
      const holidayType = holiday.holidayType || "OPTIONAL";

      return {
        holidayId: holiday.holidayId,
        holidayName: (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <ColoredCheckbox
              checked={selectedHolidays.includes(holiday.holidayId)}
              onChange={(e) =>
                handleHolidayCheckboxChange(holiday.holidayId, e.target.checked)
              }
              color="green"
            />
            <span>{holiday.holidayName}</span>
          </div>
        ),
        eventDate: holiday.eventDate,
        noOfdays: holiday.noOfdays,
        holidayType,
        branches: <BranchBadges branches={holiday.branches} />,
        actions: (
          <div className="action-buttons">
            <IconButton
              icon="iconamoon:edit-light"
              onClick={() => handleEdit(holiday.holidayId)}
              variant="edit"
              title="Edit Holidays"
            />
            <IconButton
              icon="fluent:delete-28-regular"
              onClick={() => handleDelete(holiday.holidayId)}
              variant="delete"
              title="Delete Holidays"
            />
          </div>
        ),
        searchableText:
          `${holiday.holidayName} ${holiday.eventDate} ${holiday.noOfdays} ${holidayType} ${branchNames}`.toLowerCase(),
      };
    });
  };

  const handleEdit = (holidayId) => {
    const holidayToEdit = holidays.find((h) => h.holidayId === holidayId);
    if (holidayToEdit) {
      setHolidayName(holidayToEdit.holidayName);
      setEventDate(holidayToEdit.eventDate);
      setNoOfdays(holidayToEdit.noOfdays.toString());
      setBranchIds(holidayToEdit.branches.map((branch) => branch.branchId));
      setHolidayType(
        holidayToEdit.holidayType
          ? holidayToEdit.holidayType.toUpperCase()
          : "OPTIONAL",
      );
      setEditingHolidayId(holidayId);
      setIsFormVisible(true);
      setFormErrors({});
    }
  };

  const handleDelete = async (holidayId) => {
    setShowWarningPopup(true);
    setEditingHolidayId(holidayId);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/${editingHolidayId}`,
      );
      const updatedResponse = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/holidays/all`,
      );
      setHolidays(updatedResponse.data.data);
      toast.success("Holiday deleted successfully");
      setEditingHolidayId(null);
      if (updatedResponse.data.data.length > 0) {
        markPageAsCompleted("Holidays");
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
    } finally {
      setShowWarningPopup(false);
    }
  };

  const data = transformData(filteredHolidays);

  const columns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ColoredCheckbox
            checked={isAllSelected}
            onChange={(e) => handleSelectAllHolidays(e.target.checked)}
            color="green"
          />
          <span>Holidays</span>
        </div>
      ),
      accessor: "holidayName",
    },
    { header: "Event Date", accessor: "eventDate" },
    { header: "Number of Days", accessor: "noOfdays" },
    {
      header: "Holiday Type",
      accessor: "holidayType",
      renderCell: (row) => (
        <span className={`holiday-type-badge ${row.holidayType.toLowerCase()}`}>
          {row.holidayType}
        </span>
      ),
    },
    {
      header: "Branches",
      accessor: "branches",
      renderCell: (row) => <BranchBadges branches={row.branches} />,
    },
    { header: "Actions", accessor: "actions" },
  ];

  return (
    <div className="holidays-container">
      <Heading className="display6">Holiday Management</Heading>
      <div className="holidays-header">
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            className="create-holiday-button"
            onClick={() => {
              setIsFormVisible((prev) => {
                if (prev) {
                  resetForm();
                }
                return !prev;
              });
            }}
            style={
              isFormVisible ? { backgroundColor: "#ff6692", color: "#fff" } : {}
            }
          >
            {isFormVisible ? (
              <>Close Holiday Form</>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="icon"
                  width="16"
                  height="16"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Holiday
              </>
            )}
          </button>
          <ButtonWithIcon
            text={`Bulk Delete (${selectedHolidays.length})`}
            icon="fluent:delete-28-regular"
            className="cancel-button"
            onClick={handleBulkDeleteClick}
            disabled={selectedHolidays.length === 0}
            variant="danger"
          />
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      {isFormVisible && (
        <div className="w-full p-8 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <form className="holiday-form" onSubmit={handleSubmit}>
            <div className="holiday-form__header">
              <h2 className="holiday-form__title">
                {editingHolidayId ? "Edit Holiday" : "Create New Holiday"}
              </h2>
              <p className="holiday-form__subtitle">
                {editingHolidayId
                  ? "Update the holiday details below"
                  : "Fill in the details to create a new holiday"}
              </p>
            </div>
            <div className="holiday-form__content">
              <div className="holiday-form__section">
                <h3 className="holiday-form__section-title">
                  Holiday Information
                </h3>
                <div className="holiday-form__field holiday-form__field--full-width">
                  <label className="holiday-form__label">
                    Holiday Name *
                    <input
                      type="text"
                      value={holidayName}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                      placeholder="Enter holiday name (e.g., Christmas, Diwali)"
                      onChange={(e) => {
                        setHolidayName(e.target.value);
                        if (formErrors.holidayName) {
                          setFormErrors((prev) => ({
                            ...prev,
                            holidayName: null,
                          }));
                        }
                      }}
                      required
                    />
                    {formErrors.holidayName && (
                      <span className="holiday-form__error">
                        {formErrors.holidayName}
                      </span>
                    )}
                  </label>
                </div>
                <div className="holiday-form__row">
                  <div className="holiday-form__field">
                    <label className="holiday-form__label">
                      Event Date *
                      <input
                        type="date"
                        value={eventDate}
                        onChange={(e) => {
                          setEventDate(e.target.value);
                          if (formErrors.eventDate) {
                            setFormErrors((prev) => ({
                              ...prev,
                              eventDate: null,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                        required
                      />
                      {formErrors.eventDate && (
                        <span className="holiday-form__error">
                          {formErrors.eventDate}
                        </span>
                      )}
                    </label>
                  </div>
                  <div className="holiday-form__field">
                    <label className="holiday-form__label">
                      Number of Days *
                      <input
                        type="number"
                        value={noOfdays}
                        onChange={(e) => {
                          setNoOfdays(e.target.value);
                          if (formErrors.noOfdays) {
                            setFormErrors((prev) => ({
                              ...prev,
                              noOfdays: null,
                            }));
                          }
                        }}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                        placeholder="Enter number of days"
                        min="1"
                        required
                      />
                      {formErrors.noOfdays && (
                        <span className="holiday-form__error">
                          {formErrors.noOfdays}
                        </span>
                      )}
                    </label>
                  </div>
                </div>
                <div className="holiday-form__field">
                  <label className="holiday-form__label">
                    Holiday Type *
                    <select
                      value={holidayType}
                      onChange={(e) => {
                        setHolidayType(e.target.value.toUpperCase());
                        if (formErrors.holidayType) {
                          setFormErrors((prev) => ({
                            ...prev,
                            holidayType: null,
                          }));
                        }
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
                      required
                    >
                      <option value="">Select holiday type</option>
                      <option value="OPTIONAL">Optional</option>
                      <option value="MANDATORY">Mandatory</option>
                    </select>
                    {formErrors.holidayType && (
                      <span className="holiday-form__error">
                        {formErrors.holidayType}
                      </span>
                    )}
                  </label>
                </div>
              </div>
              <div className="holiday-form__section">
                <h3 className="holiday-form__section-title">
                  Branch Assignment
                </h3>
                <p className="holiday-form__section-description">
                  Select which branches this holiday applies to
                </p>
                {renderBranchesSection()}
              </div>
            </div>
            <div className="holiday-form__actions">
              <button
                type="button"
                onClick={onCancel}
                className="holiday-form__btn holiday-form__btn--cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="holiday-form__btn holiday-form__btn--save"
                disabled={loading || branches.length === 0}
              >
                {editingHolidayId ? "Update Holiday" : "Create Holiday"}
              </button>
            </div>
          </form>
        </div>
      )}
      <div className="content-section">
        <GeneralTable
          data={data}
          columns={columns}
          title="Holiday"
          selectedCount={selectedHolidays.length}
          initialPageSize={10}
          showPagination={true}
        />
      </div>
      {showWarningPopup && (
        <WarningPopup
          isOpen={showWarningPopup}
          onClose={() => setShowWarningPopup(false)}
          onAccept={handleDeleteConfirmed}
          onDecline={() => setShowWarningPopup(false)}
          title="Confirm Delete"
          message="Are you sure you want to delete this holiday? This action cannot be undone."
          acceptText="Yes, Delete"
          declineText="Cancel"
        />
      )}
      {showBulkDeleteWarning && (
        <WarningPopup
          isOpen={showBulkDeleteWarning}
          onClose={() => setShowBulkDeleteWarning(false)}
          onAccept={handleBulkDeleteSubmit}
          onDecline={() => setShowBulkDeleteWarning(false)}
          title="Confirm Bulk Delete"
          message={`Are you sure you want to delete ${selectedHolidays.length} selected holidays? This action cannot be undone.`}
          acceptText="Yes, Delete"
          declineText="Cancel"
        />
      )}
    </div>
  );
};

export default Holidays;
